import styled from 'styled-components';

/**
 * Per-row match count shared by the transcript header's two filter menus —
 * Highlights (`SavedSegmentsFilterDropdown`) and Story Topics
 * (`TopicFilterDropdown`). They sit side by side in the toolbar and open the
 * same kind of list, so their rows must count the same way; they had drifted
 * into a pill on one and plain muted "(n)" text on the other (PLAT-11182).
 *
 * `margin-left: auto` pins it right, so the counts form a column down the menu
 * instead of trailing each row's label at a different x.
 *
 * Note this is the ROW count. The trigger's selection count is a different
 * thing — plain muted "(n)" beside the label — and is already consistent
 * across both menus.
 */
export const FilterRowCount = styled.span`
  margin-left: auto;
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 700;
  color: var(--text-muted);
  background-color: var(--surface-elevated);
  border-radius: 8px;
  min-width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  font-variant-numeric: tabular-nums;
`;

/**
 * Filter pill + checkbox menu — the homepage "Recent Content" pattern
 * (`Home/RecentContentFilters.tsx`, where these styles originate), documented
 * in docs/design-system.md "Filter Pills (Checkbox Dropdown)". A compact
 * 28px pill in the compact-row tier (`--text-xs` medium, secondary text) with
 * a gold caret; an active filter turns gold (border, 12% tint, text) — gold is
 * the design system's visual-state colour, never green, which means action.
 *
 * The menu carries no positioning of its own: portal it with
 * `useAnchoredDropdown` (pass its style) or wrap it absolutely, as the
 * homepage does.
 */
export const FilterPill = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px;
  border-radius: 8px;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-primary);
  cursor: pointer;
  white-space: nowrap;
  max-width: 200px;
  border: 1px solid ${p => (p.$active ? 'var(--accent)' : 'var(--border)')};
  background-color: ${p =>
    p.$active
      ? 'color-mix(in srgb, var(--accent) 12%, transparent)'
      : 'transparent'};
  color: ${p => (p.$active ? 'var(--accent)' : 'var(--text-secondary)')};
  transition: border-color var(--transition-fast);

  &:hover:not(:disabled) {
    border-color: ${p => (p.$active ? 'var(--accent)' : 'var(--text-muted)')};
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  svg {
    flex-shrink: 0;
  }
`;

export const FilterPillLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** Selection count after the label: plain muted figures, not a badge. */
export const FilterPillCount = styled.span`
  color: var(--text-muted);
  font-weight: var(--font-weight-normal);
  font-variant-numeric: tabular-nums;
`;

/* Rounded-row menu surface per "Dropdown / Popover Menus": small container
   padding + row gap, no divider lines. */
export const FilterMenu = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 180px;
  max-width: 320px;
  max-height: 280px;
  overflow-y: auto;
  background-color: var(--background);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 4px;
  box-shadow: var(--elevation-toast);
`;

export const FilterMenuSearch = styled.input`
  box-sizing: border-box;
  width: 100%;
  height: 30px;
  min-height: 30px;
  flex-shrink: 0;
  padding: 0 10px;
  margin-bottom: 4px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background-color: transparent;
  color: var(--foreground);
  font-size: var(--text-xs);
  font-family: var(--font-primary);
  outline: none;
  transition: border-color var(--transition-fast);

  &::placeholder {
    color: var(--text-muted);
  }
  &:hover {
    border-color: var(--text-muted);
  }
  &:focus {
    border-color: var(--focus-border);
    box-shadow: 0 0 0 1px var(--focus-ring);
  }
`;

export const FilterCheckRow = styled.button`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
  width: 100%;
  min-height: 30px;
  padding: 0 10px;
  border: none;
  border-radius: 6px;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: background-color 120ms ease;

  &:hover {
    background-color: var(--surface-elevated);
  }
`;

export const FilterCheckbox = styled.span<{ $checked: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 3px;
  border: 1px solid ${p => (p.$checked ? 'var(--accent)' : 'var(--border)')};
  background-color: ${p => (p.$checked ? 'var(--accent)' : 'transparent')};
  flex-shrink: 0;
  transition: all var(--transition-fast) ease;
`;

export const FilterRowLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--text-xs);
  color: var(--text-secondary);
`;

/** "Clear <facet>" at the top of a menu with a selection: a quiet text row. */
export const FilterMenuClear = styled.button`
  align-self: flex-start;
  min-height: 24px;
  padding: 0 10px;
  border: none;
  background: transparent;
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-family: var(--font-primary);
  cursor: pointer;

  &:hover {
    color: var(--foreground);
  }
`;

export const FilterMenuEmpty = styled.div`
  padding: 6px 10px;
  color: var(--text-muted);
  font-size: var(--text-xs);
`;
